import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  CircleSlash,
  Info,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '../components/Badge';
import { useDebounce } from '../hooks/useDebounce';
import type {
  AgentPromptMigration,
  AgentPrompt,
  AgentPromptVersionSummary,
  PromptCheckResult,
  PromptCheckSeverity,
  PromptCheckStatus,
  PromptSaveBlocker,
} from '../hooks/useAdminData';
import {
  useAgentPrompts,
  useAgentPromptVersion,
  useAgentPromptVersions,
  useEvaluateAgentPrompt,
  useGenerateAgentPromptMigration,
  useOrganizations,
  useRollbackAgentPrompt,
  useToggleAgentPrompt,
  useUpdateAgentPrompt,
} from '../hooks/useAdminData';
import { sectionTestId, splitComposedPrompt } from '../lib/composedPrompt';
import { diffLines } from '../lib/promptDiff';

/**
 * Long enough that a sentence being typed is one request rather than thirty,
 * short enough that the panel still feels attached to the editor.
 */
const STATIC_REVIEW_DEBOUNCE_MS = 350;

function triggerDownload(dirName: string, sql: string) {
  const blob = new Blob([sql], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${dirName}.sql`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatAgentType(agentType: string) {
  return agentType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function truncate(text: string, maxLength: number) {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

function isConflict(error: unknown) {
  return (error as { response?: { status?: number } })?.response?.status === 409;
}

function formatTimestamp(value: string) {
  return new Date(value).toLocaleString();
}

const DIFF_ROW_LABEL = {
  added: 'Added line',
  removed: 'Removed line',
  unchanged: 'Unchanged line',
} as const;

const DIFF_ROW_STYLE = {
  added: 'bg-green-500/10 text-green-600',
  removed: 'bg-red-500/10 text-red-600',
  unchanged: 'text-text-muted',
} as const;

function PromptDiff({ before, after }: { before: string; after: string }) {
  const lines = diffLines(before, after);

  return (
    <div
      data-testid="prompt-diff"
      className="bg-background border border-border rounded-lg max-h-60 overflow-auto"
    >
      {lines.map((line, index) => (
        <div
          key={`${line.type}-${index}-${line.text}`}
          className={`flex gap-2 px-3 py-0.5 text-xs font-mono whitespace-pre-wrap ${DIFF_ROW_STYLE[line.type]}`}
        >
          <span className="sr-only">{DIFF_ROW_LABEL[line.type]}</span>
          <span aria-hidden="true">{`${line.marker} ${line.text}`}</span>
        </div>
      ))}
    </div>
  );
}

function VersionHistory({
  agentType,
  currentPrompt,
  currentVersion,
}: {
  agentType: string;
  currentPrompt: string;
  currentVersion: number;
}) {
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [confirmingRestore, setConfirmingRestore] = useState(false);
  const { data: versions, isLoading } = useAgentPromptVersions(agentType);
  const { data: selected } = useAgentPromptVersion(agentType, selectedVersion);
  const rollbackMutation = useRollbackAgentPrompt();

  function handleSelect(version: AgentPromptVersionSummary) {
    setConfirmingRestore(false);
    setSelectedVersion((current) => (current === version.version ? null : version.version));
  }

  function handleRestore() {
    if (selectedVersion === null) return;
    rollbackMutation.mutate(
      { agentType, data: { version: selectedVersion, expectedVersion: currentVersion } },
      { onSuccess: () => setConfirmingRestore(false) },
    );
  }

  return (
    <div className="pt-2 border-t border-border space-y-3">
      <h3 className="text-sm font-medium text-text-secondary">Version history</h3>

      {isLoading && <p className="text-xs text-text-muted">Loading version history...</p>}

      {!isLoading && (!versions || versions.length === 0) && (
        <p className="text-xs text-text-muted">
          No saved versions yet. The catalog baseline is captured the first time this prompt is
          saved.
        </p>
      )}

      {versions && versions.length > 0 && (
        <ul className="space-y-1 max-h-40 overflow-auto">
          {versions.map((version) => (
            <li key={version.id}>
              <button
                type="button"
                onClick={() => handleSelect(version)}
                aria-pressed={selectedVersion === version.version}
                className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                  selectedVersion === version.version
                    ? 'border-primary bg-primary-muted'
                    : 'border-border hover:bg-surface-hover'
                }`}
              >
                <span className="block text-xs font-medium text-text">
                  Version {version.version}
                </span>
                <span className="block text-xs text-text-muted">
                  {formatTimestamp(version.createdAt)}
                </span>
                <span className="block text-xs text-text-muted">{version.createdBy ?? 'System'}</span>
                {version.changeReason && (
                  <span className="block text-xs text-text-secondary">{version.changeReason}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <div className="space-y-2">
          <p className="text-xs text-text-muted">
            Differences from version {selected.version} to the text currently in the editor.
          </p>
          <PromptDiff before={selected.prompt} after={currentPrompt} />

          {rollbackMutation.error && (
            <p role="alert" className="text-xs text-red-500">
              {isConflict(rollbackMutation.error)
                ? 'Someone else edited this prompt while you were reading it. Reload before restoring.'
                : 'Failed to restore this version. Please try again.'}
            </p>
          )}

          {confirmingRestore ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-secondary">
                Restore version {selected.version} as a new version?
              </span>
              <button
                type="button"
                onClick={handleRestore}
                disabled={rollbackMutation.isPending}
                className="bg-primary text-white rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-primary-hover transition-colors disabled:opacity-50"
              >
                {rollbackMutation.isPending ? 'Restoring...' : 'Confirm restore'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRestore(false)}
                className="border border-border rounded-lg px-3 py-1.5 text-xs text-text-secondary hover:text-text transition-colors"
              >
                Keep current
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingRestore(true)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text hover:bg-surface-hover transition-colors"
            >
              Restore
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const SEVERITY_ORDER: PromptCheckSeverity[] = ['blocker', 'high', 'medium', 'info'];

const SEVERITY_LABEL: Record<PromptCheckSeverity, string> = {
  blocker: 'Blocker',
  high: 'High',
  medium: 'Medium',
  info: 'Info',
};

const SEVERITY_HEADING: Record<PromptCheckSeverity, string> = {
  blocker: 'Blockers',
  high: 'High severity',
  medium: 'Medium severity',
  info: 'Info',
};

const SEVERITY_STYLE: Record<PromptCheckSeverity, string> = {
  blocker: 'border-red-500/40 bg-red-500/5',
  high: 'border-orange-500/40 bg-orange-500/5',
  medium: 'border-yellow-500/40 bg-yellow-500/5',
  info: 'border-border bg-background',
};

// Every status carries a word and a distinct icon. Colour alone would leave the
// difference between "failed" and "not applicable" invisible to a reader who
// cannot see it, and those two mean opposite things.
const STATUS_LABEL: Record<PromptCheckStatus, string> = {
  pass: 'Passed',
  fail: 'Failed',
  warn: 'Warning',
  not_applicable: 'Not applicable',
};

const STATUS_ICON: Record<PromptCheckStatus, typeof Info> = {
  pass: CheckCircle2,
  fail: XCircle,
  warn: AlertTriangle,
  not_applicable: CircleSlash,
};

const STATUS_ICON_STYLE: Record<PromptCheckStatus, string> = {
  pass: 'text-green-600',
  fail: 'text-red-600',
  warn: 'text-yellow-600',
  not_applicable: 'text-text-muted',
};

function CheckItem({ check }: { check: PromptCheckResult }) {
  const StatusIcon = STATUS_ICON[check.status];

  return (
    <li
      data-testid={`check-${check.id}`}
      className={`rounded-lg border px-3 py-2 space-y-1 ${SEVERITY_STYLE[check.severity]}`}
    >
      <div className="flex items-start gap-2">
        <StatusIcon
          size={14}
          aria-hidden="true"
          className={`mt-0.5 shrink-0 ${STATUS_ICON_STYLE[check.status]}`}
        />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-text">{check.title}</p>
          <p className="text-[10px] uppercase tracking-wide text-text-muted">
            {SEVERITY_LABEL[check.severity]} · {STATUS_LABEL[check.status]}
          </p>
        </div>
      </div>
      <p className="text-xs text-text-secondary">{check.message}</p>
      {check.suggestion && (
        <p className="text-xs text-text-secondary">
          <span className="font-medium">Suggestion: </span>
          {check.suggestion}
        </p>
      )}
      {check.evidence && (
        <p className="text-[11px] font-mono text-text-muted bg-background border border-border rounded px-2 py-1 whitespace-pre-wrap break-words">
          {check.evidence}
        </p>
      )}
    </li>
  );
}

function RecommendationsPanel({
  checks,
  summary,
  isStale,
  isEvaluating,
  judgeRunning,
  failed,
  onRunFullReview,
}: {
  checks: PromptCheckResult[] | undefined;
  summary: string | undefined;
  isStale: boolean;
  isEvaluating: boolean;
  judgeRunning: boolean;
  failed: boolean;
  onRunFullReview: () => void;
}) {
  return (
    <div data-testid="prompt-recommendations" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-text-secondary">Recommendations</h3>
        <button
          type="button"
          onClick={onRunFullReview}
          disabled={judgeRunning}
          className="border border-border rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {judgeRunning ? 'Running full review...' : 'Run full review'}
        </button>
      </div>

      <p className="text-xs text-text-muted">
        The fast checks re-run as you type. A full review also asks the LLM judge for the subjective
        checks, which takes a few seconds.
      </p>

      {failed && (
        <p role="alert" className="text-xs text-red-500">
          The review could not run. The prompt was not graded — fix the connection and try again.
        </p>
      )}

      {isStale && !failed && (
        <p className="text-xs text-text-muted">
          {isEvaluating ? 'Reviewing the current draft...' : 'These results are for an earlier draft.'}
        </p>
      )}

      {summary && <p className="text-xs text-text-secondary">{summary}</p>}

      {checks && checks.length === 0 && (
        <p className="text-xs text-text-muted">No findings for this prompt.</p>
      )}

      {SEVERITY_ORDER.map((severity) => {
        const group = (checks ?? []).filter((check) => check.severity === severity);
        if (group.length === 0) return null;

        return (
          <div key={severity} data-testid={`check-group-${severity}`} className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              {SEVERITY_HEADING[severity]} ({group.length})
            </h4>
            <ul className="space-y-2">
              {group.map((check) => (
                <CheckItem key={check.id} check={check} />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function ComposedPreview({
  composedPrompt,
  hasOrganization,
}: {
  composedPrompt: string | undefined;
  hasOrganization: boolean;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-text-secondary">Composed prompt</h3>
      <p className="text-xs text-text-muted">
        {hasOrganization
          ? 'What the agent will actually run for this organization. The composer appends its sections after the base prompt.'
          : 'Pick an organization to see the sections the composer appends after this prompt.'}
      </p>

      {composedPrompt === undefined ? (
        <p className="text-xs text-text-muted">Not composed yet.</p>
      ) : (
        <div data-testid="composed-preview" className="space-y-2 max-h-80 overflow-auto">
          {splitComposedPrompt(composedPrompt).map((section) => (
            <div
              key={section.title}
              data-testid={sectionTestId(section.title)}
              className={`rounded-lg border px-3 py-2 ${
                section.appended ? 'border-primary/40 bg-primary-muted/40' : 'border-border bg-background'
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <h4 className="text-xs font-medium text-text">{section.title}</h4>
                {section.appended && (
                  <span className="text-[10px] uppercase tracking-wide text-primary">
                    Appended by the composer
                  </span>
                )}
              </div>
              <p className="text-[11px] font-mono text-text-muted whitespace-pre-wrap break-words">
                {section.body}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The 422 body the save path returns when unresolved blockers stop a write. */
function saveBlockers(error: unknown): PromptSaveBlocker[] {
  const response = (
    error as { response?: { status?: number; data?: { blockers?: PromptSaveBlocker[] } } }
  )?.response;

  if (response?.status !== 422) return [];
  return response.data?.blockers ?? [];
}

function BlockerGate({
  blockers,
  overriding,
  onOverridingChange,
  reason,
  onReasonChange,
  reasonError,
}: {
  blockers: PromptSaveBlocker[];
  overriding: boolean;
  onOverridingChange: (value: boolean) => void;
  reason: string;
  onReasonChange: (value: string) => void;
  reasonError: string | null;
}) {
  return (
    <div
      data-testid="save-blockers"
      className="rounded-lg border border-red-500/40 bg-red-500/5 px-3 py-3 space-y-3"
    >
      <div className="flex items-start gap-2">
        <XCircle size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-red-600" />
        <p className="text-sm font-medium text-text">
          {blockers.length === 1
            ? 'This prompt has 1 blocking defect.'
            : `This prompt has ${blockers.length} blocking defects.`}{' '}
          A blocking defect means the agent's answer will not parse.
        </p>
      </div>

      <ul className="space-y-2">
        {blockers.map((blocker) => (
          <li
            key={blocker.id}
            data-testid={`save-blocker-${blocker.id}`}
            className="rounded-lg border border-border bg-background px-3 py-2 space-y-1"
          >
            <p className="text-xs font-medium text-text">{blocker.title}</p>
            <p className="text-xs text-text-secondary">{blocker.message}</p>
            {blocker.suggestion && (
              <p className="text-xs text-text-secondary">
                <span className="font-medium">Suggestion: </span>
                {blocker.suggestion}
              </p>
            )}
          </li>
        ))}
      </ul>

      <div className="space-y-1">
        <div className="flex items-start gap-2">
          <input
            id="agent-prompt-override-blockers"
            type="checkbox"
            checked={overriding}
            onChange={(e) => onOverridingChange(e.target.checked)}
            className="mt-0.5"
          />
          <label
            htmlFor="agent-prompt-override-blockers"
            className="text-xs font-medium text-text-secondary"
          >
            Save anyway, accepting these blocking defects
          </label>
        </div>
        <p className="text-xs text-text-muted pl-6">
          The override and your reason are recorded in the audit log against your admin account.
        </p>
      </div>

      {overriding && (
        <div className="space-y-1">
          <label
            htmlFor="agent-prompt-override-reason"
            className="block text-xs font-medium text-text-secondary"
          >
            Why are you overriding these blockers?
          </label>
          <textarea
            id="agent-prompt-override-reason"
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            rows={2}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text resize-y focus:outline-none focus:border-primary"
            placeholder="Required. Recorded with the override..."
          />
          {reasonError && (
            <p data-testid="override-reason-error" role="alert" className="text-xs text-red-500">
              {reasonError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ToggleSwitch({
  enabled,
  onToggle,
  disabled,
}: {
  enabled: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-primary' : 'bg-border'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function PromptCard({
  prompt,
  onEdit,
  onToggle,
  toggling,
}: {
  prompt: AgentPrompt;
  onEdit: () => void;
  onToggle: () => void;
  toggling: boolean;
}) {
  const migrationMutation = useGenerateAgentPromptMigration();

  function handleGenerateMigration() {
    migrationMutation.mutate(prompt.agentType, {
      onSuccess: (data: AgentPromptMigration) => {
        triggerDownload(data.dirName, data.sql);
      },
    });
  }

  return (
    <div
      className={`bg-surface rounded-xl border border-border p-5 space-y-3 ${!prompt.isActive ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-lg ${prompt.isActive ? 'bg-primary-muted' : 'bg-border'}`}
          >
            <Bot size={20} className={prompt.isActive ? 'text-primary' : 'text-text-muted'} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-text">{prompt.name}</h3>
            <p className="text-xs text-text-muted">{formatAgentType(prompt.agentType)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Badge variant={prompt.isDefault ? 'default' : 'primary'}>
            {prompt.isDefault ? 'Default' : `v${prompt.version}`}
          </Badge>
          <ToggleSwitch enabled={prompt.isActive} onToggle={onToggle} disabled={toggling} />
        </div>
      </div>

      {prompt.description && <p className="text-xs text-text-secondary">{prompt.description}</p>}

      <div className="bg-background border border-border rounded-lg px-3 py-2">
        <p className="text-xs text-text-muted font-mono leading-relaxed">
          {truncate(prompt.prompt, 120)}
        </p>
      </div>

      {migrationMutation.error && (
        <p className="text-xs text-red-500">Failed to generate migration. Please try again.</p>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`inline-block w-2 h-2 rounded-full ${prompt.isActive ? 'bg-green-500' : 'bg-red-500'}`}
          />
          <span className="text-xs text-text-muted">
            {prompt.isActive ? 'Visible to clients' : 'Hidden from clients'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleGenerateMigration}
            disabled={migrationMutation.isPending}
            className="border border-border text-text-secondary rounded-lg px-4 py-2 text-sm font-medium hover:text-text hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {migrationMutation.isPending ? 'Generating...' : 'Generate migration'}
          </button>
          <button
            onClick={onEdit}
            className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors"
          >
            Edit
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ prompt, onClose }: { prompt: AgentPrompt; onClose: () => void }) {
  const [name, setName] = useState(prompt.name);
  const [description, setDescription] = useState(prompt.description ?? '');
  const [promptText, setPromptText] = useState(prompt.prompt);
  const [changeReason, setChangeReason] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [overriding, setOverriding] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideReasonError, setOverrideReasonError] = useState<string | null>(null);
  const [judgeRunning, setJudgeRunning] = useState(false);
  const [gradedPrompt, setGradedPrompt] = useState<string | null>(null);

  const updateMutation = useUpdateAgentPrompt();
  const evaluation = useEvaluateAgentPrompt();
  const { mutate: runEvaluation } = evaluation;
  const { data: organizations } = useOrganizations({ limit: 100 });

  const debouncedPrompt = useDebounce(promptText, STATIC_REVIEW_DEBOUNCE_MS);

  // A full review is slow. Letting the debounced static run land on top of it
  // would replace the judge's findings with a report that never had them.
  const judgeRunningRef = useRef(false);

  useEffect(() => {
    if (!debouncedPrompt.trim() || judgeRunningRef.current) return;

    runEvaluation(
      {
        agentType: prompt.agentType,
        data: {
          prompt: debouncedPrompt,
          organizationId: organizationId || undefined,
          includeJudge: false,
        },
      },
      { onSuccess: () => setGradedPrompt(debouncedPrompt) },
    );
  }, [debouncedPrompt, organizationId, prompt.agentType, runEvaluation]);

  function handleFullReview() {
    judgeRunningRef.current = true;
    setJudgeRunning(true);

    runEvaluation(
      {
        agentType: prompt.agentType,
        data: {
          prompt: promptText,
          organizationId: organizationId || undefined,
          includeJudge: true,
        },
      },
      {
        onSuccess: () => setGradedPrompt(promptText),
        onSettled: () => {
          judgeRunningRef.current = false;
          setJudgeRunning(false);
        },
      },
    );
  }

  const blockers = saveBlockers(updateMutation.error);

  function handleSave() {
    if (overriding && !overrideReason.trim()) {
      setOverrideReasonError(
        'Give a reason for this override. It is recorded against your admin account.',
      );
      return;
    }
    setOverrideReasonError(null);

    updateMutation.mutate(
      {
        agentType: prompt.agentType,
        data: {
          name,
          prompt: promptText,
          description: description || undefined,
          changeReason: changeReason || undefined,
          expectedVersion: prompt.version,
          ...(overriding
            ? { overrideBlockers: true, overrideReason: overrideReason.trim() }
            : {}),
        },
      },
      { onSuccess: () => onClose() },
    );
  }

  function handleRevert() {
    setPromptText(prompt.defaultPrompt);
    setName(prompt.name);
    setDescription(prompt.description ?? '');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-xl shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text">
            Edit Prompt: {formatAgentType(prompt.agentType)}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
          >
            <span className="sr-only">Close</span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
            <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">
              Agent Type
            </label>
            <div className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-text-muted">
              {formatAgentType(prompt.agentType)}
            </div>
          </div>

          <div>
            <label
              htmlFor="agent-prompt-organization"
              className="block text-sm font-medium text-text-secondary mb-1.5"
            >
              Compose for organization
            </label>
            <select
              id="agent-prompt-organization"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
            >
              <option value="">None — grade the base prompt alone</option>
              {(organizations?.data ?? []).map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-muted mt-1">
              This prompt is saved for every tenant. Pick an organization to review it as that
              tenant will compose it.
            </p>
          </div>

          <div>
            <label
              htmlFor="agent-prompt-name"
              className="block text-sm font-medium text-text-secondary mb-1.5"
            >
              Display Name
            </label>
            <input
              id="agent-prompt-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label
              htmlFor="agent-prompt-description"
              className="block text-sm font-medium text-text-secondary mb-1.5"
            >
              Description
            </label>
            <textarea
              id="agent-prompt-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text resize-y focus:outline-none focus:border-primary"
              placeholder="Optional description of what this prompt does..."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label
                htmlFor="agent-prompt-text"
                className="block text-sm font-medium text-text-secondary"
              >
                System Prompt
              </label>
              <span className="text-xs text-text-muted">{promptText.length} characters</span>
            </div>
            <textarea
              id="agent-prompt-text"
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              rows={10}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text font-mono resize-y focus:outline-none focus:border-primary leading-relaxed"
            />
          </div>

          <div>
            <label
              htmlFor="agent-prompt-change-reason"
              className="block text-sm font-medium text-text-secondary mb-1.5"
            >
              Reason for this change
            </label>
            <input
              id="agent-prompt-change-reason"
              type="text"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
              placeholder="Optional, stored alongside this version..."
            />
          </div>

          {updateMutation.error && blockers.length === 0 && (
            <p role="alert" className="text-sm text-red-500">
              {isConflict(updateMutation.error)
                ? 'Someone else edited this prompt while you had it open. Reload the page to pick up their version before saving again.'
                : 'Failed to save the prompt. Please try again.'}
            </p>
          )}

          {blockers.length > 0 && (
            <BlockerGate
              blockers={blockers}
              overriding={overriding}
              onOverridingChange={setOverriding}
              reason={overrideReason}
              onReasonChange={setOverrideReason}
              reasonError={overrideReasonError}
            />
          )}

          <VersionHistory
            agentType={prompt.agentType}
            currentPrompt={promptText}
            currentVersion={prompt.version}
          />
            </div>

            <div className="space-y-5">
              <ComposedPreview
                composedPrompt={evaluation.data?.composedPrompt}
                hasOrganization={Boolean(organizationId)}
              />
              <RecommendationsPanel
                checks={evaluation.data?.checks}
                summary={evaluation.data?.summary}
                isStale={gradedPrompt !== null && gradedPrompt !== promptText}
                isEvaluating={evaluation.isPending}
                judgeRunning={judgeRunning}
                failed={Boolean(evaluation.error)}
                onRunFullReview={handleFullReview}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <button
              onClick={handleRevert}
              className="flex items-center gap-2 text-sm text-text-secondary hover:text-text transition-colors"
            >
              <RotateCcw size={14} />
              Revert to Default
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:text-text hover:bg-surface-hover transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending || !name.trim() || !promptText.trim()}
                className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AgentPrompts() {
  const { data: prompts, isLoading } = useAgentPrompts();
  const [editingPrompt, setEditingPrompt] = useState<AgentPrompt | null>(null);
  const toggleMutation = useToggleAgentPrompt();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Agent Prompts</h1>
          <p className="text-sm text-text-muted mt-1">
            Manage default system prompts for AI agents
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-surface rounded-xl border border-border p-5 h-48 animate-pulse"
            >
              <div className="h-4 bg-border rounded w-40" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!prompts) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Agent Prompts</h1>
          <p className="text-sm text-text-muted mt-1">
            Manage default system prompts for AI agents
          </p>
        </div>
        <p className="text-text-muted">Failed to load agent prompts.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Agent Prompts</h1>
        <p className="text-sm text-text-muted mt-1">Manage default system prompts for AI agents</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {prompts.map((prompt) => (
          <PromptCard
            key={prompt.agentType}
            prompt={prompt}
            onEdit={() => setEditingPrompt(prompt)}
            onToggle={() => toggleMutation.mutate(prompt.agentType)}
            toggling={toggleMutation.isPending}
          />
        ))}
      </div>

      {editingPrompt && (
        <EditModal
          key={editingPrompt.agentType}
          prompt={editingPrompt}
          onClose={() => setEditingPrompt(null)}
        />
      )}
    </div>
  );
}
